import { useCallback, useEffect, useMemo, useState } from 'react';

import { getCampaignReplaceVariables } from 'services/MailingService';
import { getLiteral } from 'utils/getLiteral';
import { transformVariablesToMergeTags } from '../utils';

/**
 * Hook to load and transform campaign variables from the backend
 * to use as mergeTags in the Topol editor.
 *
 * @param {Object} options
 * @param {boolean} options.isOpen - Whether the editor is open
 * @returns {Object} { variables, loading, error, retry, isSingleEntityMode, singleEntityKey }
 */
export const useTopolVariables = ({ isOpen }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [rawVariables, setRawVariables] = useState(null);

    const isSingleEntityMode = useMemo(() => {
        if (!rawVariables || typeof rawVariables !== 'object' || Array.isArray(rawVariables)) {
            return false;
        }
        return Object.keys(rawVariables).length === 1;
    }, [rawVariables]);

    const singleEntityKey = useMemo(() => {
        if (!isSingleEntityMode || !rawVariables) return null;
        return Object.keys(rawVariables)[0];
    }, [isSingleEntityMode, rawVariables]);

    const fetchVariables = useCallback(() => {
        if (!isOpen) return;

        setLoading(true);
        setError(null);

        getCampaignReplaceVariables()
            .then((data) => {
                setRawVariables(data);
            })
            .catch((err) => {
                console.error('Error loading campaign replace variables:', err);
                setError(err);
            })
            .finally(() => setLoading(false));
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && !rawVariables && !error) {
            fetchVariables();
        }
    }, [isOpen, rawVariables, error, fetchVariables]);

    const variables = useMemo(() => {
        if (!rawVariables) return null;
        const mergeTags = transformVariablesToMergeTags(rawVariables);
        if (isSingleEntityMode && mergeTags.length === 1) {
            return [
                {
                    ...mergeTags[0],
                    name: getLiteral('title_insert_fields'),
                },
            ];
        }
        return mergeTags;
    }, [isSingleEntityMode, rawVariables]);

    const retry = useCallback(() => {
        setError(null);
        fetchVariables();
    }, [fetchVariables]);

    return {
        variables,
        loading,
        error,
        retry,
        isSingleEntityMode,
        singleEntityKey,
    };
};

export default useTopolVariables;
